import { useState, type FormEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { adminAddTrackingEvent, type TrackingMilestone } from '../../api/endpoints/admin';
import type { PlaceDetail } from '../../api/endpoints/places';
import { AddressAutocompleteField } from '../../components/domain/AddressAutocompleteField';
import { useMutationHook } from '../../hooks/useMutationHook';
import { getAvailableMilestones } from './trackingMilestones';
import styles from '../../components/domain/FormModal.module.css';
import { DialogShell } from '../../components/ui/DialogShell';
import { useDialogDismiss, useIsDirty } from '../../components/ui/dialogDismiss';
import { DialogTitleRow } from '../../help/DialogTitleRow';

interface Props {
  shipmentId: number;
  shipmentStatus?: string;
  onClose: () => void;
}

/**
 * Ops-logged milestone event (e.g. correcting a scan the driver missed).
 * Milestones drive the shipment state machine, filtered to only allow next
 * valid steps based on the shipment's current status.
 * Location is selected via Google Places autocomplete rather than manual coordinates.
 */
export function TrackingEventModal({ shipmentId, shipmentStatus, onClose }: Props) {
  const { t } = useTranslation();
  const availableMilestones = getAvailableMilestones(shipmentStatus);
  const [selectedMilestone, setSelectedMilestone] = useState<TrackingMilestone | null>(null);
  const effectiveMilestone: TrackingMilestone =
    selectedMilestone && availableMilestones.some((m) => m.value === selectedMilestone)
      ? selectedMilestone
      : (availableMilestones[0]?.value ?? 'EN_ROUTE_PICKUP');
  const [addressText, setAddressText] = useState('');
  const [selectedPlace, setSelectedPlace] = useState<PlaceDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutationHook(
    () =>
      adminAddTrackingEvent(
        shipmentId,
        effectiveMilestone,
        selectedPlace?.lat ?? undefined,
        selectedPlace?.lng ?? undefined,
        selectedPlace?.formattedAddress || addressText.trim() || undefined,
      ),
    {
      successMessage: 'Tracking milestone logged.',
      invalidateKeys: [
        ['admin', 'shipments', 'tracking', shipmentId],
        ['admin', 'shipments', 'detail', shipmentId],
        ['admin', 'shipments'],
      ],
      onSuccess: onClose,
    },
  );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (
      availableMilestones.length === 0 ||
      !availableMilestones.some((m) => m.value === effectiveMilestone)
    ) {
      return setError('No valid forward milestone can be logged for this shipment status.');
    }
    setError(null);
    saveMut.mutate(undefined);
  };

  const dirty = useIsDirty({ selectedMilestone, addressText });
  const dismiss = useDialogDismiss({ dirty, busy: saveMut.isLoading, onClose });

  return (
    <DialogShell
      dismiss={dismiss}
      label={t('shipments.trackingEventModal.title')}
      scrimClassName={styles.scrim}
      panelClassName={styles.panel}
    >
      <DialogTitleRow context="trip-status" title={t('shipments.trackingEventModal.title')}>
        <h2 className={styles.title}>{t('shipments.trackingEventModal.title')}</h2>
      </DialogTitleRow>

      <form onSubmit={submit}>
        {availableMilestones.length > 0 ? (
          <>
            <label className={styles.label}>{t('shipments.trackingEventModal.milestone')}</label>
            <select
              className={styles.select}
              value={effectiveMilestone}
              onChange={(e) => setSelectedMilestone(e.target.value as TrackingMilestone)}
            >
              {availableMilestones.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </>
        ) : (
          <div
            style={{
              padding: '10px 12px',
              marginBottom: 12,
              borderRadius: 'var(--radius-button)',
              background: 'var(--status-amber-bg)',
              border: '1px solid var(--status-amber-border)',
              color: 'var(--status-amber-text)',
              fontSize: 13,
            }}
          >
            {shipmentStatus === 'AT_PICKUP'
              ? 'Shipment is At Pickup. The transition to Loaded is handled automatically upon verifying loading documents.'
              : `No manual milestones available for current shipment status: ${shipmentStatus || 'Unknown'}.`}
          </div>
        )}

        <div style={{ marginTop: 14 }}>
          <AddressAutocompleteField
            label={t('shipments.trackingEventModal.locationLabel')}
            placeholder={t('shipments.trackingEventModal.locationPlaceholder')}
            value={addressText}
            onChange={setAddressText}
            onPlaceSelected={(detail) => setSelectedPlace(detail)}
            onCleared={() => setSelectedPlace(null)}
          />
          {selectedPlace && (
            <div
              style={{
                marginTop: 6,
                padding: '6px 10px',
                background: 'var(--status-green-bg)',
                border: '1px solid var(--status-green-border)',
                borderRadius: 'var(--radius-button)',
                fontSize: 11.5,
                color: 'var(--ink)',
              }}
            >
              {t('shipments.trackingEventModal.selected')} <strong>{selectedPlace.city || selectedPlace.formattedAddress}</strong>{' '}
              <span style={{ color: 'var(--muted)' }}>
                {t('shipments.trackingEventModal.coordinates', {
                  lat: selectedPlace.lat?.toFixed(4) ?? '',
                  lng: selectedPlace.lng?.toFixed(4) ?? '',
                })}
              </span>
            </div>
          )}
        </div>

        <p className={styles.hint} style={{ marginTop: 12 }}>
          <Trans
            i18nKey="shipments.trackingEventModal.hint"
            values={{ status: shipmentStatus || t('shipments.trackingEventModal.currentStatus') }}
            components={{ strong: <strong /> }}
          />
        </p>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={dismiss.requestClose}>
            {t('common.actions.cancel')}
          </button>
          <button
            type="submit"
            className={styles.submit}
            disabled={saveMut.isLoading || availableMilestones.length === 0}
          >
            {saveMut.isLoading ? 'Logging…' : 'Log event'}
          </button>
        </div>
      </form>
    </DialogShell>
  );
}
